import { API_BASE_URL } from "../constant/constant";

/**
 * Fetch available voices for a language
 * @param {string} language - The language name (e.g., "English", "Swahili")
 * @param {string} [token] - Optional user authentication token
 * @returns {Promise<object>} Object containing language, default voice, and voices list array
 */
export const getVoices = async (language, token) => {
  const languageLower = language.toLowerCase();
  const headers = {
    "Accept": "application/json",
  };
  if (token) {
    headers["Authorization"] = `Bearer ${token}`;
  }

  try {
    const response = await fetch(`${API_BASE_URL}/tts/v1/${languageLower}/voices`, {
      method: "GET",
      headers,
    });

    if (response.ok) {
      return await response.json();
    }
  } catch (err) {
    console.warn("Backend /tts/v1/:language/voices error, attempting direct fallback:", err);
  }
};


/**
 * Generate speech from text (Text-to-Speech)
 * @param {string} text - The input text to synthesize
 * @param {string} language - The language name (e.g., "English", "Yoruba", "Swahili", "Nigerian Pidgin")
 * @param {string} voiceName - The identifier of the selected voice model
 * @param {string} token - The user authentication token
 * @returns {Promise<Blob>} The generated audio file blob
 */
export const generateSpeech = async (text, language, voiceName, token) => {
  const languageLower = language.toLowerCase();
  const API_URL = `${API_BASE_URL}/tts/v1/${languageLower}`;

  const payload = {
    text,
    voice_name: voiceName,
    speaking_rate: 1,
  };

  const response = await fetch(API_URL, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(errorData.message || errorData.error || "Speech generation failed.");
  }

  return await response.blob();
};

/**
 * Transcribe speech to text (Speech-to-Text)
 * @param {File} audioFile - The audio file to transcribe
 * @param {string} language - The language name (e.g., "English", "Yoruba", "Swahili", "Nigerian Pidgin")
 * @param {string} token - The user authentication token
 * @param {number} [duration] - Optional measured duration in seconds
 * @returns {Promise<object>} The transcription result
 */
export const transcribeSpeech = async (audioFile, language, token, duration) => {
  const languageLower = language.toLowerCase();
  const formData = new FormData();
  formData.append("audio_file", audioFile);
  if (Number.isFinite(duration) && duration > 0) {
    formData.append("duration", duration);
  }

  const response = await fetch(`${API_BASE_URL}/stt/v1/${languageLower}`, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
    },
    body: formData,
  });

  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(errorData.message || errorData.error || "Transcription failed.");
  }

  return await response.json();
};

/**
 * Translate speech to English text (Speech-to-Text Translation / Speech Translation)
 * @param {File} audioFile - The audio file to translate
 * @param {string} pair - The translation pair (e.g., "swahili-to-english", "yoruba-to-english", "pidgin-to-english")
 * @param {string} token - The user authentication token
 * @param {number} [duration] - Optional measured duration in seconds
 * @returns {Promise<object>} The translation result
 */
export const translateSpeech = async (audioFile, pair, token, duration) => {
  const pairLower = pair.toLowerCase().replace(/\s+/g, '-');
  const formData = new FormData();
  formData.append("audio_file", audioFile);
  if (Number.isFinite(duration) && duration > 0) {
    formData.append("duration", duration);
  }

  const response = await fetch(`${API_BASE_URL}/st/v1/${pairLower}`, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
    },
    body: formData,
  });

  if (!response.ok) {
    let errorMessage = "Speech translation failed.";
    try {
      const errorData = await response.json();
      errorMessage = errorData.message || errorData.error || errorMessage;
    } catch (_) { }
    throw new Error(errorMessage);
  }

  const contentType = response.headers.get("content-type");
  if (contentType && (contentType.includes("audio") || contentType.includes("octet-stream"))) {
    return await response.blob();
  }

  return await response.json();
};
